import { useTranslation } from 'react-i18next'

import { faDigits } from '../../lib/numerals'
import { UNKNOWN_WORD } from '../../lib/repeatMatch'
import { normalizeWord } from '../../lib/words'

/**
 * Where a practice that repeats stands (R13, owner decision 2026-10-13): a
 * dot per repetition and «۳/۵», and — while the learner speaks and the take
 * is checked — the live text the on-device recognizer types. Nothing else:
 * no rule, no «آخری», no verdict sentence (owner, 2026-10-14: «جملات اضافه و
 * توضیحی نمیخوام توی صفحه»). The dots move on a counted repetition; a wrong
 * or too-short one is a short toast from the page —
 * and after a wrong one the sentence's words stay up, the missed ones red
 * (owner, 2026-10-15): the word itself once the text is open
 * (`textOpen`), a red blank while it is still locked.
 *
 * Presentational only — everything comes from `useShadow().repetition` and
 * `.liveText`, so the page test can render each state with plain props.
 * The live text is Latin, so it gets its own direction inside the RTL page;
 * a word of the sentence is drawn in the "good" ink as it is heard, `[unk]`
 * (a stretch the recognizer could not place) as a faint ellipsis.
 */
export default function RepeatPanel({ repetition, liveText, recording = false, textOpen = false }) {
  const { t } = useTranslation('lesson')
  const { required, done, number, checking, lastRep } = repetition
  // A single-take practice (practice.repeatCount 1) shows the live text alone — every take is typed (owner, 2026-10-14).
  const single = !repetition.on
  const sentenceWords = new Set(liveText.target ?? [])
  const words = liveText.words ?? []
  const live = recording || checking
  const missed = !single && !live && lastRep && !lastRep.counted && Array.isArray(lastRep.words) ? lastRep.words : null

  if (single && !live) return null

  return (
    <div className="mb-4 space-y-3">
      {!single && (
        <div className="flex items-center justify-center gap-2.5" role="img" aria-label={t('repeat.counterAria', { done: faDigits(done), total: faDigits(required) })}>
          <span className="flex gap-1.5" aria-hidden="true">
            {Array.from({ length: required }, (_, index) => (
              <span
                key={index}
                data-rep-dot={index < done ? 'done' : index === done ? 'current' : 'todo'}
                className={`size-2.5 rounded-full ${
                  index < done ? 'bg-good' : index === done ? 'bg-brand-600 ring-2 ring-brand-200' : 'bg-line'
                }`}
              />
            ))}
          </span>
          <span dir="ltr" aria-hidden="true" data-rep-counter className="tnum text-sm font-bold text-ink">
            {faDigits(number)}/{faDigits(required)}
          </span>
        </div>
      )}

      {missed && (
        <div className="rounded-xl border border-bad/40 bg-surface-2 px-3 py-2.5" data-rep-missed>
          <p dir="ltr" lang="en" className="lesson-text ltr text-lg leading-relaxed">
            {missed.map(({ word, heard }, index) =>
              heard ? (
                <span key={index} data-word="heard" className="text-good-ink">
                  {word}{' '}
                </span>
              ) : (
                <span key={index} data-word="missing" className="font-semibold text-bad-ink underline decoration-bad decoration-2 underline-offset-4">
                  {textOpen ? word : '_____'}{' '}
                </span>
              ),
            )}
          </p>
        </div>
      )}

      {live && (
        <div className="min-h-[3.25rem] rounded-xl border border-line bg-surface-2 px-3 py-2.5" data-live-text>
          <p dir="ltr" lang="en" className="lesson-text ltr text-lg leading-relaxed" aria-live="polite">
            {words.length === 0 ? (
              <span className="text-ink-3">…</span>
            ) : (
              words.map((word, index) =>
                word === UNKNOWN_WORD ? (
                  <span key={index} className="text-ink-3">
                    {'… '}
                  </span>
                ) : (
                  <span key={index} data-live-word={sentenceWords.has(normalizeWord(word)) ? 'heard' : 'other'} className={sentenceWords.has(normalizeWord(word)) ? 'text-good-ink' : 'text-ink-2'}>
                    {word}{' '}
                  </span>
                ),
              )
            )}
          </p>
        </div>
      )}
    </div>
  )
}
